/**
 * Created by Ning Jiang on 2021-05-04.
 */

import {TombstoneRipParsedData} from "./TombstoneRipParsedData";
import {IBetWayWinData, ISuperFsInfo, ITombstoneRipGameData} from "./ITombstoneRipData";
import {TombstoneRipDataParser} from "./TombstoneRipDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {TombstoneRipGameConfig} from "../../TombstoneRipGameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TombstoneRipGameMode} from "../../gameflow/TombstoneRipGameMode";
import {StackedSymbol} from "../../../core/reel/stackedsymbol/StackedSymbol";

export class TombstoneRipParsedGameData extends TombstoneRipParsedData {

    public readonly betWins:IBetWayWinData[];
    public readonly betWinsBeforeTwoScatter:boolean[][][];
    public readonly singleWin:number;
    public readonly playedBetValue:number;
    public readonly wasFeatureBuy:boolean;
    public readonly nearWinReels:boolean[];
    public readonly nearWinBigWinReels:boolean[];
    public readonly doReelNudge:boolean[];
    public readonly nudgeSteps:number[];
    public readonly xSplitPosition:boolean[][];
    public readonly convertScatterToReelSplit:number[];
    public readonly reelSplit:boolean[];
    public readonly brokeTheBank:boolean;
    public readonly superFSInfo:ISuperFsInfo;
    public readonly currentWinRatioConfig:IWinRatioConfig | null; // Calculated values

    constructor(rawData:ITombstoneRipGameData, parser:TombstoneRipDataParser) {
        super(rawData, parser);

        this.playedBetValue = rawData.playedBetValue;
        this.betWins = rawData.betWayWins;
        this.singleWin = parser.parseSingleWin(this.playedBetValue, this.betWins, rawData.xRip);
        this.wasFeatureBuy = rawData.wasFeatureBuy;
        this.nearWinReels = parser.parseNearWinReels(this.scatterPositions, this.wasFeatureBuy);
        this.doReelNudge = parser.parseDoReelNudge(rawData);
        this.nudgeSteps = this.parseNudgeSteps();
        this.xSplitPosition = rawData.xSplitPosition.concat();
        this.convertScatterToReelSplit = parser.parseConvertScatterToReelSplit(this.scatterPositions, this.mode);
        this.superFSInfo = rawData.superFSInfo;
        if(this.superFSInfo) {
            // https://github.com/nolimitcity/tombstonerip/issues/361
            this.removeUpgradeMNextToNudge();
        }
        this.betWinsBeforeTwoScatter = this.getBetWinsBeforeTwoScatter();
        this.reelSplit = parser.parseReelSplit(rawData, this.convertScatterToReelSplit);
        this.brokeTheBank = rawData.brokeTheBank;
        this.currentWinRatioConfig = parser.parseWinRatio(this.singleWin / this.playedBetValue);
        this.nearWinBigWinReels = this.getRandomBigWinNearWin();
    }

    private getBetWinsBeforeTwoScatter():boolean[][][] {
        const winsBeforeReveal:boolean[][][] = [];
        if(!this.betWins || this.betWins.length === 0) {
            return winsBeforeReveal;
        }
        this.betWins.forEach(w => {
            const winPositions = ArrayHelper.initArrayWithValues<boolean[]>(TombstoneRipGameConfig.REELS_NUM,
                reelId => w.reelWinPositions[reelId].concat()
            );

            // remove if win contains the ReelSplit from 2xS1
            this.convertScatterToReelSplit.forEach((symId, reelId) => {
                if(symId > -1) {
                    winPositions[reelId][symId] = false;
                }
            });

            // remove if win contains MW which comes from the S2 respin.
            if(this.superFSInfo && this.superFSInfo.respinReel5) {
                [0, 1, 2, 3].forEach(reelId => {
                    const symbolIds = reelId === 0 ? [1, 2] : [1, 2, 3];
                    symbolIds.forEach(symId => {
                        if(this.reels[reelId][symId] === this.superFSInfo.outlawWild) {
                            winPositions[reelId][symId - 1] = false;
                        }
                    });
                });
            }

            for(let rId = 0; rId < w.length; rId++) {
                if(winPositions[rId].every(s => !s)) {
                    if(rId < 3) {
                        return;
                    }
                    rId++;
                    while(rId < w.length) {
                        winPositions[rId] = [false, false, false];
                        rId++;
                    }
                    break;
                }
            }

            winsBeforeReveal.push(winPositions);
        });

        // If the S2 respin W was already a win case before reveal.
        if(this.superFSInfo && this.superFSInfo.respinReel5) {
            const respinWildWins = ArrayHelper.initArrayWithValues<boolean[]>(TombstoneRipGameConfig.REELS_NUM,
                reelId => [false, false, false]
            );
            [0, 1, 2, 3].forEach(reelId => {
                const symbolIds = reelId === 0 ? [1, 2] : [1, 2, 3];
                symbolIds.forEach(symId => {
                    if(this.reels[reelId][symId] === this.superFSInfo.outlawWild) {
                        respinWildWins[reelId][symId - 1] = true;
                    }
                });
            });

            let shallAdd = true;
            for(let rId = 0; rId < 4; rId++) {
                if(respinWildWins[rId].every(s => !s)) {
                    if(rId < 3) {
                        shallAdd = false;
                        break;
                    }
                    rId++;
                    while(rId < 4) {
                        respinWildWins[rId] = [false, false, false];
                        rId++;
                    }
                    break;
                }
            }
            if(shallAdd) {
                winsBeforeReveal.push(respinWildWins);
            }
        }
        return winsBeforeReveal;
    }

    private removeUpgradeMNextToNudge():void {
        this.doReelNudge.forEach((doNudge, reelId) => {
            if(!doNudge) {
                return
            }

            for(let i = 1; i < 4; i++) {
                if(this.reels[reelId][i] === this.superFSInfo.outlawMultiplier) {
                    this.reels[reelId][i] = "L5";
                }
            }
        });
    }

    // The range is the startIndex (include) in the reel symbols to the endIndex (NOT include).
    // For a 3x5 slot, there are 3 symbols in reel, then the range is [0, 3],
    // If you have a special feature that the are actually 5 symbols but you only show 3 symbols from the bottom of the 5 symbols, then you should return [2, 5].
    private parseNudgeSteps():number[] {
        const checkRange:[number, number] = [1, 4];
        return ArrayHelper.initArrayWithValues(TombstoneRipGameConfig.REELS_NUM, reelId => {
            let symbolIndex:number = -1;
            let stackIndex:number = -1;
            for(let i:number = checkRange[0]; i < checkRange[1]; i++) {
                const stackedSymbolName = StackedSymbol.parseStackedSymbolName(this.reels[reelId][i], reelId);
                if(["W1", "W2", "W3"].includes(stackedSymbolName.symName)) {
                    symbolIndex = i;
                    stackIndex = stackedSymbolName!.index;
                    break;
                }
            }

            if(symbolIndex === -1) {
                return 0;
            }

            return checkRange[0] + stackIndex - symbolIndex;
        });
    }

    private getRandomBigWinNearWin():boolean[] {
        const values = ArrayHelper.initArrayWithValues(TombstoneRipGameConfig.REELS_NUM, () => false);
        values[4] = this.getRandomBigWinTease();
        if(this.singleWin > 0 && this.mode === TombstoneRipGameMode.FREESPIN_SUPER) {
            values[3] = true;
        }

        return values;
    }

    private getRandomBigWinTease():boolean {
        if(this.wasFeatureBuy) {
            return false;
        }
        if(this.totalWin === 0 || this.reels[4][1] === "S2") {
            return false;
        }
        switch(this.mode) {
            case GameMode.NORMAL:
                return this.currentWinRatioConfig!.ratio + Math.random() * 15 > 35;
            case GameMode.FREESPIN:
                return this.currentWinRatioConfig!.ratio + Math.random() * 20 > 50;
            case TombstoneRipGameMode.FREESPIN_SUPER:
                return false; // super fs reel 5 stop the first!
            default:
                return false;
        }
    }
}